import { FC, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { trackEvent } from 'dibs-tracking';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import classNames from 'classnames';

import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespTilesSwiper } from '../SbRespTilesSwiper/SbRespTilesSwiper';
import { SbRespDesignsDescriptionTile } from './SbRespDesignsDescriptionTile';
import { SbRespDesignTiles_itemSearch$data } from './__generated__/SbRespDesignTiles_itemSearch.graphql';
import { SbRespDesignTiles_designPageInfo$data } from './__generated__/SbRespDesignTiles_designPageInfo.graphql';

import styles from './SbRespDesignTiles.scss';

type TileType = NonNullable<SbRespDesignTiles_itemSearch$data['creatorDesignTiles']>[0];

type Props = {
    itemSearch: SbRespDesignTiles_itemSearch$data;
    designPageInfo?: SbRespDesignTiles_designPageInfo$data;
    onViewMoreClick?: () => void;
};

const FULL_ROW_LENGTH = 4;

const SbRespDesignTilesComponent: FC<Props> = ({ itemSearch, designPageInfo, onViewMoreClick }) => {
    const { creatorDesignTiles = [], displayUriRef } = itemSearch;
    const { isTablet } = useContext(ServerVarsContext);
    if (!creatorDesignTiles || creatorDesignTiles.length < 2) {
        return null;
    }
    const hasRelatedDesigns = designPageInfo?.creatorDesigns?.length;

    return (
        <div
            className={classNames(styles.container, {
                [styles.hasFullRowOfTiles]: creatorDesignTiles.length === FULL_ROW_LENGTH,
            })}
        >
            <SbRespTilesSwiper displayUriRef={displayUriRef || ''} isTablet={isTablet}>
                <SbRespDesignsDescriptionTile
                    itemSearch={itemSearch}
                    onViewMoreClick={hasRelatedDesigns ? onViewMoreClick : undefined}
                />
                {creatorDesignTiles.map((tile: TileType, index: number) => {
                    const { displayName, linkData, imageUrl } = tile || {};
                    if (!displayName || !linkData || !imageUrl) {
                        return null;
                    }
                    return (
                        <SeoLink
                            className={styles.tile}
                            linkData={linkData}
                            key={displayName}
                            dataTn={`design-tile-link-${index}`}
                            onClick={() =>
                                trackEvent({
                                    category: 'promo interaction',
                                    action: 'more from iconic design creator clicked',
                                    label: 'creator page',
                                })
                            }
                        >
                            <div className={styles.imageWrapper}>
                                <img className={styles.image} src={imageUrl} alt={displayName} />
                            </div>
                            <div className={styles.title}>{displayName}</div>
                        </SeoLink>
                    );
                })}
            </SbRespTilesSwiper>
        </div>
    );
};

export const SbRespDesignTiles = createFragmentContainer(SbRespDesignTilesComponent, {
    itemSearch: graphql`
        fragment SbRespDesignTiles_itemSearch on ItemSearchQueryConnection {
            ...SbRespDesignsDescriptionTile_itemSearch
            displayUriRef
            creatorDesignTiles {
                displayName
                imageUrl
                linkData {
                    ...SeoLink_linkData
                }
            }
        }
    `,
    designPageInfo: graphql`
        fragment SbRespDesignTiles_designPageInfo on DesignPageInfo {
            creatorDesigns {
                urlLabel
            }
        }
    `,
});
